import { LC_CONVERSATIONS } from '@mm/api-routes';
import { FORBIDDEN, UNAUTHORIZED } from '@mm/constants/status-codes';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import {
  ContactsSearchResult,
  ConversationsSearchResult,
  ConversationsSearchResultMatch,
  LCAttributeFilter,
  LCConversationSubset,
  LCView,
  useLiveChatCRUDActions,
} from '@mm/state/actions/LiveChatCRUDActions';
import { useSessionCRUDActions } from '@mm/state/actions/SessionCRUDActions';
import {
  GetFilteredConversationsParams,
  getHeaders,
} from '@mm/state/controllers/LiveChatController';
import JSONUtils from '@mm/state/helpers/json-utils';
import { Conversation } from '@mm/state/interfaces/conversations';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import {
  LiveChatChannel,
  LivechatConversationModel,
} from '@mm/state/model/LivechatConversationModel';
import { LivechatMessageModel } from '@mm/state/model/LivechatMessageModel';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { isEqual } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import { useHistory } from 'react-router-dom';
import {
  ActionObject,
  assign,
  createMachine,
  DoneInvokeEvent,
  send,
  spawn,
} from 'xstate';
import { pure } from 'xstate/lib/actions';
import { parseMessageStructureAndCampaign } from './components/Messages/parseMessage';
import {
  ContactActor,
  ContactContext,
  createContactMachine,
  EmptySelectedContact,
} from './contactMachine';
import {
  ConversationActor,
  ConversationContext,
  createConversationMachine,
  initialConversationContext,
} from './conversationMachine';
import {
  remapConversations,
  remapMessages,
  RemapMessagesDataReturn,
} from './shared';

export type LCFetchStatus = {
  moreToFetch: boolean;
  nextPageToFetch: number;
};

const fetchStatus: LCFetchStatus = {
  moreToFetch: true,
  nextPageToFetch: 0,
};

export const initialFetchStatus = {
  main: fetchStatus,
  follow_up: fetchStatus,
  filtered: fetchStatus,
};

export type LcConversationSubset = 'main' | 'follow_up' | 'filtered';
export type LcConversationState =
  | 'main'
  | 'follow_up'
  | 'pending'
  | 'resolved'
  | 'done';

export type LcContext = {
  lastSSEUpdate: number;
  userId: number;
  userAuthToken: string;
  // Works as the ID for selectedContact, Conversation, streaming messages
  remoteUserId: string | undefined;
  onlineRemoteUserIds: Set<string>;
  sessionSuffix: string | undefined;
  conversationsRefs: Record<string, ConversationActor>;
  contactsRefs: Record<string, ContactActor>;
  activeContact: string | undefined;
  mostRecentConversationsRemoteUserIds: Set<string>;
  filteredConversationRemoteUserIds: Set<string>;
  fetchStatus: Record<LCConversationSubset, LCFetchStatus>;
  batchConversationMessagesToLoad: {
    remoteUserId: string;
    pageState: string | null;
  }[];
  currentConversationsBatchToLoad: {
    remoteUserId: string;
    pageState: string | null;
  }[];
  searchResultsMessages: LivechatMessageModel[];
  searchResultMatches: ConversationsSearchResultMatch[];
  currentConversationLoadingParams: null | GetFilteredConversationsParams;
  needToCreateInitiaConversationAndContact: boolean;

  // Redux selectors
  remoteFbPageId: string | undefined;
  connectedPage?: ConnectedPageModel;
  activeChatbotId: number;
  botLanguage: string;
  searchString: string;
  foundConversations: ConversationsSearchResult | undefined;
  foundContacts: ContactsSearchResult[];
  activeBotDefaultPageId?: string;

  conversationSubset: LcConversationSubset;
  conversationState: LcConversationState;
  selectedChannels: LiveChatChannel[];
  selectedTags: string[];
  selectedAttribute: LCAttributeFilter | null;
  selectedAssignee: 'unassigned' | 'all' | number;
};

export enum LcSseStates {
  init = 'init',
  connected = 'connected',
  error = 'error',
  reconnect = 'reconnect',
}

export enum LcSearchStates {
  idle = 'idle',
  searching = 'searching',
}

// Note; this is a helper to aid in having better TS typings for context based
// on state value. I have only a very basic/rudimentary part here, but we can add more
// details if/when needed.

type OnSuccessData =
  | {
      message: string;
      sessionSuffix: string;
    }
  | undefined;

const SseEventTypes = {
  open: 'open',
  error: 'error',
  success: 'success',
  conversations: 'conversations',
  messages: 'messages',
  message: 'message',
  close: 'close',
  online_users: 'online_users',
} as const;

type LcRefilterRedirect = {
  type: 'LC_REFILTER_REDIRECT';
  payload: {
    filterParameters: GetFilteredConversationsParams;
  };
};

type LcConversationsGetMoreRedirect = {
  type: 'LC_CONVERSATIONS_GET_MORE_REDIRECT';
  payload: GetFilteredConversationsParams;
};

type LcRemoteUserIdChange = {
  type: 'LC_REMOTE_USER_ID_CHANGE';
  payload: string | undefined;
};

type LcGetSearchResultSession = {
  type: 'GET_SEARCH_RESULT_SESSION';
  payload: {
    remUserId: string | undefined;
    sessionId: number;
    addingOlder: boolean;
    reset?: boolean;
  };
};

type LcViewUpdatePayload = {
  viewUpdate: Partial<
    Pick<
      LcContext,
      | 'selectedTags'
      | 'selectedAttribute'
      | 'selectedAssignee'
      | 'conversationState'
      | 'conversationSubset'
      | 'selectedChannels'
    >
  >;
  skipStoringAttributes?: boolean;
};

export type LcEventSourceError = {
  type: 'LC_ERROR';
  payload: EventSourceEventMap['error'] & {
    error: {
      message: string;
      status?: number;
    };
  };
};

export type LcViewUpdate = {
  type: 'LC_VIEW_UPDATE';
  payload: LcViewUpdatePayload;
};

export type LcStoreView = {
  type: 'LC_STORE_VIEW';
  payload: LcViewUpdatePayload['viewUpdate'];
};

export type LcSseSuccess = {
  type: 'LC_SUCCESS';
  payload: EventSourceEventMap['message'];
};

export type LcConversations = {
  type: 'LC_CONVERSATIONS';
  payload: LivechatConversationModel[] | undefined;
};

export type LcConversationsAndContactCreation = {
  type: 'LC_CONVERSATION_AND_CONTACT_CREATE';
  payload: string;
};

export type LcRemoteFbPageIdChange = {
  type: 'LC_REMOTE_FB_PAGE_ID_CHANGE';
  payload: string | undefined;
};

export type LcBotIdChange = { type: 'LC_BOT_ID_CHANGE'; payload: number };

export type LcBotDefaultPageIdChange = {
  type: 'LC_BOT_DEFAULT_PAGE_ID_CHANGE';
  payload: string | undefined;
};

export type LcEventSchema =
  | LcRemoteUserIdChange
  | LcGetSearchResultSession
  | { type: 'LC_CONTEXT_UPDATE'; payload: Partial<LcContext> }
  | { type: 'LC_SEARCH' }
  | { type: 'LC_LOCATION_SEARCH_CHANGE' }
  | { type: 'LC_RECONNECT_NOW' }
  | {
      type: 'LC_CONTACT_LAST_ACTIVE_UPDATE';
      payload: ReturnType<typeof parseMessageStructureAndCampaign>;
    }
  | LcConversations
  | LcRemoteFbPageIdChange
  | LcBotIdChange
  | LcBotDefaultPageIdChange
  | {
      type: 'LC_CONVERSATIONS_GET_MORE';
    }
  | {
      type: 'LC_CONVERSATIONS_GET_MESSAGES';
      payload: {
        remoteUserId: string;
        pageState: string | null;
      };
    }
  | { type: 'LC_CONVERSATIONS_REFRESH'; payload: string[] | 'all' }
  | {
      type: 'LC_CONVERSATIONS_REFILTER';
      payload: string[] | 'all';
    }
  | {
      type: 'LC_GET_CONVERSATIONS_TO_REFILTER';
      payload: LivechatConversationModel[] | undefined;
    }
  | LcRefilterRedirect
  | LcConversationsGetMoreRedirect
  | {
      type: 'LC_CONVERSATION_UPDATE';
      payload: Partial<LivechatConversationModel>;
    }
  | {
      type: 'LC_CONVERSATIONS_DELETE';
      payload: string[] | 'all';
    }
  | { type: 'LC_GET_STORED_VIEW' }
  | { type: 'LC_VIEW_BULK_RESET' }
  | LcStoreView
  | LcViewUpdate
  | LcConversationsAndContactCreation
  // SSE matching
  | { type: 'LC_OPEN'; payload: EventSourceEventMap['open'] }
  | LcEventSourceError
  | { type: 'LC_MESSAGE'; payload: EventSourceEventMap['message'] }
  | { type: 'LC_MESSAGES'; payload: EventSourceEventMap['message'] }
  | LcSseSuccess
  | { type: 'LC_ONLINE_USERS'; payload: EventSourceEventMap['message'] }
  | { type: 'LC_RECONNECT' }
  | { type: 'LC_LAST_CONVERSATION_UPDATE' }
  | { type: 'LC_CLOSE' };

export const createLivechatMachine = (
  context: LcContext,
  liveChatCRUDActions: ReturnType<typeof useLiveChatCRUDActions>,
  contactsCRUDActions: ReturnType<typeof useContactsCRUDActions>,
  sessionCRUDActions: ReturnType<typeof useSessionCRUDActions>,
  history: ReturnType<typeof useHistory>,
) =>
  createMachine<LcContext, LcEventSchema>(
    {
      id: `liveChatMachine-${Date.now()}-${Math.ceil(Math.random() * 1000)}`,
      context,
      type: 'parallel',
      states: {
        storedView: {
          initial: 'fetching',
          states: {
            idle: {},
            fetching: {
              invoke: {
                id: 'stored-view-fetching',
                src: 'storedViewFetching',
                onDone: {
                  target: 'idle',
                  actions: send(
                    (
                      ctx,
                      evt: DoneInvokeEvent<Omit<LCView, 'conversationSubset'>>,
                    ) => {
                      return {
                        type: 'LC_VIEW_UPDATE',
                        payload: {
                          viewUpdate: evt.data,
                          skipStoringAttributes: true,
                        },
                      };
                    },
                  ),
                },
                onError: {
                  target: 'idle',
                },
              },
            },
            saving: {
              invoke: {
                id: 'stored-view-saving',
                src: 'storedViewSaving',
                onDone: {
                  target: 'idle',
                },
                onError: {
                  target: 'idle',
                },
              },
            },
          },
          on: {
            LC_GET_STORED_VIEW: {
              target: '.fetching',
              internal: false,
            },
            LC_STORE_VIEW: {
              target: '.saving',
              internal: false,
            },
          },
        },

        connectionGuard: {
          initial: 'wait',
          states: {
            wait: {
              after: {
                10000: {
                  target: 'check',
                },
              },
            },
            check: {
              always: {
                target: 'wait',
                actions: pure(ctx => {
                  if (Date.now() - ctx.lastSSEUpdate > 40000) {
                    return send({ type: 'LC_VIEW_BULK_RESET' });
                  }
                }),
              },
            },
          },
        },

        mainLogic: {
          id: 'mainLogic',
          initial: 'init',
          states: {
            init: {
              entry: ['stopActors', 'contextReset'],
              always: [
                {
                  target: 'getStoredViewSettings',
                  cond: ctx => ctx.activeChatbotId > 0 && !!ctx.remoteFbPageId,
                },
              ],
            },
            getStoredViewSettings: {
              entry: [send({ type: 'LC_GET_STORED_VIEW' })],
              on: {
                LC_VIEW_UPDATE: {
                  target: 'active',
                  // Note: Resending event to next state, since there are separate listeners
                  // actions: send((ctx, evt) => evt, { delay: 30 }),
                  actions: [send((ctx, evt) => evt, { delay: 30 })],
                },
              },
            },
            active: {
              type: 'parallel',
              entry: pure((ctx, evt) => {
                if (
                  ctx.needToCreateInitiaConversationAndContact &&
                  ctx.remoteUserId
                ) {
                  return [
                    send({
                      type: 'LC_CONVERSATION_AND_CONTACT_CREATE',
                      payload: ctx.remoteUserId,
                    }),
                    assign({
                      needToCreateInitiaConversationAndContact:
                        false as boolean,
                    }),
                  ];
                }
              }),
              states: {
                contactSearch: {
                  initial: LcSearchStates.idle,
                  states: {
                    [LcSearchStates.idle]: {},
                    [LcSearchStates.searching]: {
                      invoke: {
                        id: 'contact-search',
                        src: 'contactSearch',
                        onDone: {
                          target: LcSearchStates.idle,
                          actions: pure(
                            (
                              _ctx,
                              evt: DoneInvokeEvent<
                                ContactsSearchResult[] | undefined
                              >,
                            ) => {
                              if (evt.data) {
                                return assign({ foundContacts: evt.data });
                              }
                            },
                          ),
                        },
                        onError: {
                          target: LcSearchStates.idle,
                        },
                      },
                    },
                  },
                },
                conversationSearch: {
                  initial: LcSearchStates.idle,
                  states: {
                    [LcSearchStates.idle]: {},
                    [LcSearchStates.searching]: {
                      invoke: {
                        id: 'conversation-search',
                        src: 'conversationSearch',
                        onDone: {
                          target: LcSearchStates.idle,
                          actions: pure(
                            (
                              ctx,
                              evt: DoneInvokeEvent<
                                ConversationsSearchResult | undefined
                              >,
                            ) => {
                              if (evt.data) {
                                return assign({
                                  ...ctx,
                                  foundConversations: evt.data,
                                });
                              }
                            },
                          ),
                        },
                        onError: {
                          target: LcSearchStates.idle,
                        },
                      },
                    },
                  },
                },
                followUps: {
                  initial: 'loading',
                  states: {
                    idle: {},
                    loading: {
                      invoke: {
                        id: 'load-follow-ups',
                        src: 'loadFollowUps',
                        onDone: {
                          target: 'idle',
                          actions: pure(
                            (
                              ctx,
                              evt: DoneInvokeEvent<
                                | undefined
                                | {
                                    conversations: LivechatConversationModel[];
                                  }
                              >,
                            ) => {
                              if (!evt.data || !evt.data.conversations) {
                                return;
                              }

                              return [
                                assign({
                                  fetchStatus: {
                                    ...ctx.fetchStatus,
                                    follow_up: {
                                      moreToFetch:
                                        evt.data.conversations.length > 0,
                                      nextPageToFetch: 1,
                                    },
                                  },
                                }),
                                send({
                                  type: 'LC_CONVERSATIONS',
                                  payload: evt.data.conversations,
                                }),
                              ];
                            },
                          ),
                        },
                        onError: {
                          target: 'idle',
                        },
                      },
                    },
                  },
                },

                remoteUserUpdate: {
                  id: 'remoteUserUpdateState',
                  initial: 'idle',
                  states: {
                    idle: {},
                    updating: {
                      invoke: {
                        id: 'remote-user-update-promise',
                        src: 'remoteUserUpdate',
                        onDone: {
                          target: 'idle',
                          actions: pure(
                            (
                              ctx,
                              evt: DoneInvokeEvent<RemapMessagesDataReturn>,
                            ) => {
                              if (
                                evt.data?.data?.length &&
                                ctx.conversationsRefs[
                                  evt.data.data[0].remoteUserId
                                ]
                              ) {
                                return [
                                  send(
                                    {
                                      type: 'LC_CONVERSATIONS_MESSAGES_UPDATE',
                                      payload: {
                                        data: evt.data,
                                        isRestCall: true,
                                      },
                                    },
                                    {
                                      to: `conversation-${evt.data.data[0].remoteUserId}`,
                                    },
                                  ),
                                ];
                              }
                            },
                          ),
                        },
                        onError: {
                          target: 'idle',
                        },
                      },
                    },
                  },
                  on: {
                    LC_REMOTE_USER_ID_CHANGE: [
                      {
                        target: '#remoteUserUpdateState.updating',
                        internal: false,
                        actions: 'remoteUserIdChange',
                        cond: (ctx, evt) =>
                          !!evt.payload && evt.payload !== ctx.remoteUserId,
                      },
                      {
                        actions: 'remoteUserIdChange',
                        cond: (ctx, evt) => evt.payload !== ctx.remoteUserId,
                      },
                    ],
                  },
                },

                searchResultsMessagesFetching: {
                  initial: 'idle',
                  states: {
                    idle: {},
                    fetching: {
                      invoke: {
                        id: 'search-results-messages-fetching',
                        src: 'searchResultsMessagesFetching',
                        onDone: {
                          target: 'idle',
                          actions: pure(
                            (
                              ctx,
                              evt: DoneInvokeEvent<
                                | undefined
                                | {
                                    remappedMessages: RemapMessagesDataReturn;
                                    reset: boolean;
                                    addingOlder: boolean;
                                  }
                              >,
                            ) => {
                              if (evt.data?.remappedMessages?.data) {
                                const actionsToReturn: ActionObject<
                                  Partial<LcContext>,
                                  DoneInvokeEvent<
                                    | undefined
                                    | {
                                        remappedMessages: RemapMessagesDataReturn;
                                        reset: boolean;
                                        addingOlder: boolean;
                                      }
                                  >
                                >[] = [];

                                const remappedMessages =
                                  evt.data.remappedMessages.data.map(m =>
                                    parseMessageStructureAndCampaign(m),
                                  );

                                if (evt.data.reset) {
                                  actionsToReturn.push(
                                    assign({
                                      searchResultsMessages: [
                                        ...remappedMessages,
                                      ],
                                    }),
                                  );
                                } else if (evt.data.addingOlder) {
                                  actionsToReturn.push(
                                    assign({
                                      searchResultsMessages: [
                                        ...ctx.searchResultsMessages,
                                        ...remappedMessages,
                                      ],
                                    }),
                                  );
                                } else {
                                  actionsToReturn.push(
                                    assign({
                                      searchResultsMessages: [
                                        ...remappedMessages,
                                        ...ctx.searchResultsMessages,
                                      ],
                                    }),
                                  );
                                }
                                return actionsToReturn;
                              }
                            },
                          ),
                        },
                        onError: {
                          target: 'idle',
                        },
                      },
                    },
                  },
                  on: {
                    GET_SEARCH_RESULT_SESSION: {
                      target: '.fetching',
                      internal: false,
                      cond: (ctx, evt) => !!evt.payload.remUserId,
                    },
                  },
                },

                batchedConversationMessagesLoader: {
                  initial: 'idle',
                  states: {
                    idle: {},
                    batching: {
                      after: {
                        200: [
                          {
                            target: 'fetching',
                            cond: 'hasBatchedConversationMessagesToLoad',
                            actions: assign(ctx => {
                              return {
                                currentConversationsBatchToLoad:
                                  ctx.batchConversationMessagesToLoad,
                                batchConversationMessagesToLoad: [],
                              };
                            }),
                          },
                          {
                            target: 'idle',
                            cond: 'hasNoBatchedConversationMessagesToLoad',
                          },
                        ],
                      },
                    },
                    fetching: {
                      invoke: {
                        id: 'batch-conversation-messages-loading',
                        src: 'loadBatchConversationMessages',
                        onDone: [
                          {
                            target: 'batching',
                            cond: 'hasBatchedConversationMessagesToLoad',
                            actions: 'batchedConversationMessagesUpdate',
                          },
                          {
                            target: 'idle',
                            cond: 'hasNoBatchedConversationMessagesToLoad',
                            actions: 'batchedConversationMessagesUpdate',
                          },
                        ],
                        onError: [
                          {
                            target: 'batching',
                            cond: 'hasBatchedConversationMessagesToLoad',
                          },
                          {
                            target: 'idle',
                            cond: 'hasNoBatchedConversationMessagesToLoad',
                          },
                        ],
                      },
                    },
                  },
                  on: {
                    LC_CONVERSATIONS_GET_MESSAGES: {
                      target: '.batching',
                      internal: false,
                      actions: assign((ctx, evt) => {
                        return {
                          batchConversationMessagesToLoad: [
                            ...ctx.batchConversationMessagesToLoad,
                            evt.payload,
                          ],
                        };
                      }),
                    },
                  },
                },

                sse: {
                  initial: 'init',
                  states: {
                    [LcSseStates.init]: {
                      always: {
                        target: LcSseStates.connected,
                        cond: ctx => !!ctx.remoteFbPageId,
                      },
                    },
                    [LcSseStates.connected]: {
                      invoke: {
                        id: 'sseConnection',
                        // Note: for some reason it is not narrowing down the state for invoke itself for now
                        src: (ctx, evt) => (callback, onReceive) => {
                          const { userId, userAuthToken, remoteFbPageId } = ctx;

                          const onOpen = (e: EventSourceEventMap['open']) => {
                            callback({ type: 'LC_OPEN', payload: e });
                          };

                          // Note: By default on errors browser tries to re-connect,
                          // quickly openning many connections.
                          // This creates problems with SSE, since multiple re-connects are possible.
                          // To prevent browser default re-connects, we are handling them explicitly.
                          // This allows us to better control # of connections and timeouts
                          const onError = (
                            e: EventSourceEventMap['error'] & {
                              error: {
                                message: string;
                                status?: number;
                              };
                            },
                          ) => {
                            callback({ type: 'LC_ERROR', payload: e });
                          };

                          const onMessage = (
                            e: EventSourceEventMap['message'],
                          ) => {
                            callback({ type: 'LC_MESSAGE', payload: e });
                          };

                          const onMessages = (
                            e: EventSourceEventMap['message'],
                          ) => {
                            callback({ type: 'LC_MESSAGES', payload: e });
                          };

                          const onSuccess = (
                            e: EventSourceEventMap['message'],
                          ) => {
                            callback({ type: 'LC_SUCCESS', payload: e });
                          };

                          const onClose = () => {
                            callback({ type: 'LC_CLOSE' });
                          };

                          const onConversations = (
                            e: EventSourceEventMap['message'],
                          ) => {
                            const payload = remapConversations(
                              JSONUtils.safeParse(e.data),
                            );
                            callback({
                              type: 'LC_GET_CONVERSATIONS_TO_REFILTER',
                              payload,
                            });

                            callback({
                              type: 'LC_CONVERSATIONS',
                              payload,
                            });

                            callback({
                              type: 'LC_LAST_CONVERSATION_UPDATE',
                            });
                          };

                          const onOnlineUsers = (
                            e: EventSourceEventMap['message'],
                          ) => {
                            callback({
                              type: 'LC_ONLINE_USERS',
                              payload: e,
                            });
                          };

                          const subscribeSourceToEvents = (
                            src: EventSource,
                          ) => {
                            if (src) {
                              src.addEventListener(SseEventTypes.open, onOpen);
                              src.addEventListener(
                                SseEventTypes.error,
                                onError,
                              );
                              src.addEventListener(
                                SseEventTypes.message,
                                onMessage,
                              );
                              src.addEventListener(
                                SseEventTypes.success,
                                onSuccess,
                              );
                              src.addEventListener(
                                SseEventTypes.conversations,
                                onConversations,
                              );
                              src.addEventListener(
                                SseEventTypes.messages,
                                onMessages,
                              );
                              src.addEventListener(
                                SseEventTypes.close,
                                onClose,
                              );
                              src.addEventListener(
                                SseEventTypes.online_users,
                                onOnlineUsers,
                              );
                            }
                          };

                          const unsubscribeSourceFromEvents = (
                            src: EventSource,
                          ) => {
                            if (src) {
                              // sessionSuffixContainer.current = null;
                              src.removeEventListener(
                                SseEventTypes.open,
                                onOpen,
                              );
                              src.removeEventListener(
                                SseEventTypes.error,
                                onError,
                              );
                              src.removeEventListener(
                                SseEventTypes.message,
                                onMessage,
                              );
                              src.removeEventListener(
                                SseEventTypes.success,
                                onSuccess,
                              );
                              src.removeEventListener(
                                SseEventTypes.conversations,
                                onConversations,
                              );
                              src.removeEventListener(
                                SseEventTypes.messages,
                                onMessages,
                              );
                              src.removeEventListener(
                                SseEventTypes.close,
                                onClose,
                              );
                              src.removeEventListener(
                                SseEventTypes.online_users,
                                onOnlineUsers,
                              );
                            }
                          };

                          const url = ctx.activeBotDefaultPageId
                            ? `${LC_CONVERSATIONS}?include_done=1&default_page_id=${ctx.activeBotDefaultPageId}`
                            : `${LC_CONVERSATIONS}?include_done=1`;

                          const source: EventSource = new EventSourcePolyfill(
                            url,
                            {
                              headers: getHeaders({
                                userId,
                                userAuthToken,
                                remoteFbPageId: remoteFbPageId!, // it is always defined here
                              }),
                            },
                          );

                          subscribeSourceToEvents(source);

                          // Note: Event Source connection is unsubscribed and closed on transitioning
                          // from this state into any other.
                          return () => {
                            if (source) {
                              unsubscribeSourceFromEvents(source);
                              source.close();
                            }
                          };
                        },
                      },
                      on: {
                        LC_OPEN: {
                          // actions: [(ctx, evt) => console.log('open event: ', evt.payload)],
                        },
                        LC_MESSAGE: {
                          // actions: [
                          //   (ctx, evt) => console.log('message event: ', evt.payload),
                          // ],
                        },
                        LC_MESSAGES: {
                          actions: [
                            pure((ctx, evt) => {
                              const parsedData = JSONUtils.safeParse(
                                evt.payload.data,
                              );
                              const remappedData = remapMessages(parsedData);

                              if (
                                remappedData &&
                                remappedData.data &&
                                remappedData.data.length &&
                                ctx.conversationsRefs[
                                  remappedData.data[0].remoteUserId
                                ]
                              ) {
                                return send(
                                  {
                                    type: 'LC_CONVERSATIONS_MESSAGES_UPDATE',
                                    payload: { data: remappedData },
                                  },
                                  {
                                    to: `conversation-${remappedData.data[0].remoteUserId}`,
                                  },
                                );
                              }
                            }),
                          ],
                        },
                        LC_SUCCESS: {
                          actions: 'sseSuccess',
                        },
                        LC_ONLINE_USERS: {
                          actions: [
                            pure((ctx, evt) => {
                              const parsedData = JSONUtils.safeParse(
                                evt.payload.data,
                              ) as string[];

                              const oldOnlineUsers = ctx.onlineRemoteUserIds;

                              const hasChanges =
                                oldOnlineUsers.size !== parsedData.length ||
                                parsedData.some(u => !oldOnlineUsers.has(u));
                              if (hasChanges) {
                                return assign({
                                  onlineRemoteUserIds: new Set(parsedData),
                                });
                              }
                            }),
                          ],
                        },
                        LC_CLOSE: {
                          target: LcSseStates.reconnect,
                          actions: [
                            send({ type: 'LC_RECONNECT_NOW' }, { delay: 5000 }),
                          ],
                        },
                        LC_ERROR: [
                          {
                            target: LcSseStates.error,
                            actions: 'clearSession',
                            cond: (_ctx, evt) => {
                              const { status } = evt.payload.error || {};
                              return (
                                !!status &&
                                (status === UNAUTHORIZED ||
                                  status === FORBIDDEN)
                              );
                            },
                          },
                          {
                            target: LcSseStates.reconnect,
                            cond: (_ctx, evt) => {
                              const { message } = evt.payload.error || {};
                              return (
                                message === 'network error' ||
                                message === 'Failed to fetch'
                              );
                            },
                          },
                        ],
                      },
                    },
                    [LcSseStates.error]: {
                      after: {
                        1000: {
                          target: LcSseStates.reconnect,
                          actions: send(
                            { type: 'LC_RECONNECT_NOW' },
                            { delay: 2000 },
                          ),
                        },
                      },
                    },
                    [LcSseStates.reconnect]: {
                      after: {
                        15000: { target: LcSseStates.connected },
                      },
                      on: {
                        LC_RECONNECT_NOW: { target: LcSseStates.connected },
                      },
                    },
                  },
                  on: {
                    LC_RECONNECT: {
                      target: `.${LcSseStates.reconnect}`,
                    },
                  },
                },

                view: {
                  id: 'view-specific-states',
                  initial: 'active',
                  states: {
                    active: {
                      type: 'parallel',
                      states: {
                        refiltering: {
                          initial: 'idle',
                          states: {
                            idle: {
                              on: {
                                LC_REFILTER_REDIRECT: {
                                  target: 'fetching',
                                },
                              },
                            },
                            fetching: {
                              invoke: {
                                id: 'conversations-filtering',
                                src: 'conversationsFiltering',
                                onDone: {
                                  target: 'idle',
                                  actions: pure(
                                    (
                                      ctx,
                                      evt: DoneInvokeEvent<
                                        | {
                                            conversations: LivechatConversationModel[];
                                            remoteUserIds: string[];
                                          }
                                        | undefined
                                      >,
                                    ) => {
                                      if (!evt.data) return [];

                                      const actionsToReturn: ActionObject<
                                        Partial<LcContext>,
                                        DoneInvokeEvent<
                                          | {
                                              conversations: LivechatConversationModel[];
                                              remoteUserIds: string[];
                                            }
                                          | undefined
                                        >
                                      >[] = [];

                                      const { conversations, remoteUserIds } =
                                        evt.data;

                                      const matchingRemoteUserIds = new Set(
                                        conversations.map(c => c.remoteUserId),
                                      );

                                      const filteredConversationRemoteUserIdSet =
                                        new Set([
                                          ...ctx.filteredConversationRemoteUserIds,
                                          ...matchingRemoteUserIds,
                                        ]);

                                      // Remove IDs that are no longer matching the filter
                                      if (remoteUserIds) {
                                        remoteUserIds
                                          .filter(
                                            i => !matchingRemoteUserIds.has(i),
                                          )
                                          .forEach(i =>
                                            filteredConversationRemoteUserIdSet.delete(
                                              i,
                                            ),
                                          );
                                      }

                                      actionsToReturn.push(
                                        assign({
                                          filteredConversationRemoteUserIds:
                                            filteredConversationRemoteUserIdSet,
                                        }),
                                      );

                                      if (conversations) {
                                        actionsToReturn.push(
                                          send({
                                            type: 'LC_CONVERSATIONS',
                                            payload: conversations,
                                          }),
                                        );
                                      }

                                      return actionsToReturn;
                                    },
                                  ),
                                },
                                onError: {
                                  target: 'idle',
                                },
                              },
                            },
                          },
                          on: {
                            LC_GET_CONVERSATIONS_TO_REFILTER: {
                              // @ts-ignore Note: xState typing for pure effects seems to be wrong with latest TS versions
                              actions: pure((ctx, evt) => {
                                const conversations = evt.payload;

                                if (
                                  !conversations ||
                                  ctx.conversationSubset !== 'filtered'
                                ) {
                                  return;
                                }

                                const changedRemoteUserIds: string[] = [];

                                const conversationsWithFollowUpStatusChange: Record<
                                  string,
                                  boolean
                                > = {};

                                conversations.forEach(newConversation => {
                                  const oldConversation =
                                    ctx.conversationsRefs[
                                      newConversation.remoteUserId
                                    ]?.state.context.model;

                                  if (!oldConversation) {
                                    return changedRemoteUserIds.push(
                                      newConversation.remoteUserId,
                                    );
                                  }

                                  const mapConvo = (
                                    c: LivechatConversationModel,
                                  ) => ({
                                    assignedUserId: String(
                                      c.assignedUserId || '',
                                    ),
                                    inboxStatus: c.inboxStatus,
                                    followUp: c.followUp,
                                  });

                                  if (
                                    !isEqual(
                                      mapConvo(newConversation),
                                      mapConvo(oldConversation),
                                    )
                                  ) {
                                    if (
                                      newConversation.followUp !==
                                      oldConversation.followUp
                                    ) {
                                      conversationsWithFollowUpStatusChange[
                                        newConversation.remoteUserId
                                      ] = true;
                                    }

                                    return changedRemoteUserIds.push(
                                      newConversation.remoteUserId,
                                    );
                                  }

                                  // Note: I'm turning off this logic for now, since
                                  // it triggers constant calls to filtered_conversations
                                  // on every portion of streaming conversations data from NodeJS
                                  // Search for a linked conversation
                                  // if (
                                  //   newConversation.linkedContacts
                                  //     ?.map(lc => lc.remoteUserId)
                                  //     ?.some(remoteUserId =>
                                  //       ctx.filteredConversationRemoteUserIds.has(
                                  //         remoteUserId,
                                  //       ),
                                  //     )
                                  // ) {
                                  //   changedRemoteUserIds.push(
                                  //     newConversation.remoteUserId,
                                  //   );
                                  //   newConversation.linkedContacts.forEach(lc =>
                                  //     changedRemoteUserIds.push(
                                  //       lc.remoteUserId,
                                  //     ),
                                  //   );
                                  // }
                                });

                                if (changedRemoteUserIds.length) {
                                  const actionsToReturn: ActionObject<
                                    Partial<LcContext>,
                                    LcEventSchema
                                  >[] = [
                                    send({
                                      type: 'LC_CONVERSATIONS_REFILTER',
                                      payload: changedRemoteUserIds,
                                    }),
                                  ];

                                  if (
                                    changedRemoteUserIds.find(
                                      id => ctx.remoteUserId === id,
                                    )
                                  ) {
                                    if (
                                      ctx.remoteUserId &&
                                      conversationsWithFollowUpStatusChange[
                                        ctx.remoteUserId
                                      ]
                                    ) {
                                      return actionsToReturn;
                                    }
                                    actionsToReturn.push(
                                      send({
                                        type: 'LC_REMOTE_USER_ID_CHANGE',
                                        payload: undefined,
                                      }),
                                    );
                                  }
                                  return actionsToReturn;
                                }
                              }),
                            },
                            LC_CONVERSATIONS_REFILTER: {
                              actions: pure((ctx, evt) => {
                                if (
                                  !ctx.remoteFbPageId ||
                                  !ctx.filteredConversationRemoteUserIds.size
                                ) {
                                  return;
                                }

                                const filterParameters = {
                                  conversationSubset: ctx.conversationSubset,
                                  conversationState: ctx.conversationState,
                                  assignedUserId: ctx.selectedAssignee,
                                  selectedChannels: ctx.selectedChannels,
                                  selectedTags: ctx.selectedTags,
                                  selectedAttribute: ctx.selectedAttribute,
                                  remoteUserIds: evt.payload,
                                  remotePageId: ctx.remoteFbPageId,
                                  defaultPageId: ctx.activeBotDefaultPageId,
                                  page: 0,
                                  perPage: evt.payload?.length || 20,
                                };

                                if (evt.payload !== 'all') {
                                  return send({
                                    type: 'LC_REFILTER_REDIRECT',
                                    payload: { filterParameters },
                                  });
                                }

                                return [
                                  send({
                                    type: 'LC_REMOTE_USER_ID_CHANGE',
                                    payload: undefined,
                                  }),
                                  send(
                                    { type: 'LC_VIEW_BULK_RESET' },
                                    { delay: 50 },
                                  ),
                                ];
                              }),
                            },
                          },
                        },
                        conversationsLoading: {
                          initial: 'idle',
                          states: {
                            idle: {
                              on: {
                                LC_CONVERSATIONS_GET_MORE_REDIRECT: [
                                  {
                                    target: 'loading',
                                  },
                                ],
                              },
                            },

                            loading: {
                              entry: assign(
                                (_ctx, evt: LcConversationsGetMoreRedirect) => {
                                  return {
                                    currentConversationLoadingParams:
                                      evt.payload,
                                  };
                                },
                              ),
                              exit: assign((_ctx, _evt) => {
                                return {
                                  currentConversationLoadingParams: null,
                                };
                              }),
                              on: {
                                LC_CONVERSATIONS_GET_MORE_REDIRECT: [
                                  {
                                    actions: pure((ctx, evt) => {
                                      // Note: cancelling requests with same params as the one already running
                                      if (
                                        !isEqual(
                                          evt.payload,
                                          ctx.currentConversationLoadingParams,
                                        )
                                      ) {
                                        return send(
                                          {
                                            type: 'LC_CONVERSATIONS_GET_MORE_REDIRECT',
                                            payload: evt.payload,
                                          },
                                          { delay: 100 },
                                        );
                                      }
                                    }),
                                  },
                                ],
                              },
                              invoke: {
                                id: 'conversations-loading',
                                src: 'conversationsLoading',
                                onDone: {
                                  target: 'idle',
                                  actions: pure(
                                    // @ts-ignore Note: xState typing for pure effects seems to be wrong with latest TS versions
                                    (
                                      ctx,
                                      evt: DoneInvokeEvent<{
                                        conversations:
                                          | LivechatConversationModel[]
                                          | undefined;
                                        fetchParameters: GetFilteredConversationsParams;
                                      }>,
                                    ) => {
                                      if (evt.data.conversations) {
                                        const actionsToReturn: ActionObject<
                                          Partial<LcContext>,
                                          DoneInvokeEvent<{
                                            conversations: LivechatConversationModel[];
                                            fetchParameters: GetFilteredConversationsParams;
                                          }>
                                        >[] = [];

                                        const recentUpdate =
                                          ctx.conversationSubset === 'filtered'
                                            ? {
                                                filteredConversationRemoteUserIds:
                                                  new Set([
                                                    ...ctx.filteredConversationRemoteUserIds,
                                                    ...evt.data.conversations.map(
                                                      c => c.remoteUserId,
                                                    ),
                                                  ]),
                                              }
                                            : {
                                                mostRecentConversationsRemoteUserIds:
                                                  new Set([
                                                    ...ctx.mostRecentConversationsRemoteUserIds,
                                                    ...evt.data.conversations.map(
                                                      c => c.remoteUserId,
                                                    ),
                                                  ]),
                                              };

                                        actionsToReturn.push(
                                          assign({ ...ctx, ...recentUpdate }),
                                        );

                                        actionsToReturn.push(
                                          send({
                                            type: 'LC_CONVERSATIONS',
                                            payload: evt.data.conversations,
                                          }),
                                        );

                                        if (
                                          evt.data.fetchParameters
                                            .conversationSubset
                                        ) {
                                          // Note: due to introduction of ElasticSearch we can end up being in a situation
                                          // when BE in step 1 gets 20 converesation ID from DB, but later in step 2
                                          // gets less than 20 due to some conversations being not yet indexed.
                                          // As a result we can get less than 100% of requested pageSize and still
                                          // have other pages to load.
                                          // As a result per conversation with Yevgen we should keep sending requests till we get
                                          // 0 as a response.
                                          const moreToFetch =
                                            evt.data.conversations.length > 0;
                                          actionsToReturn.push(
                                            assign({
                                              fetchStatus: {
                                                ...ctx.fetchStatus,
                                                [evt.data.fetchParameters
                                                  .conversationSubset]: {
                                                  moreToFetch,
                                                  nextPageToFetch: moreToFetch
                                                    ? evt.data.fetchParameters
                                                        .page + 1
                                                    : evt.data.fetchParameters
                                                        .page,
                                                },
                                              },
                                            }),
                                          );
                                        }

                                        return actionsToReturn;
                                      }
                                    },
                                  ),
                                },
                                onError: {
                                  target: 'idle',
                                },
                              },
                            },
                          },
                          on: {
                            LC_CONVERSATIONS_GET_MORE: {
                              actions: pure(ctx => {
                                const fetchStatus =
                                  ctx.fetchStatus[ctx.conversationSubset];

                                if (
                                  !ctx.remoteFbPageId ||
                                  !fetchStatus.moreToFetch
                                ) {
                                  return;
                                }

                                const fetchParams = {
                                  conversationSubset: ctx.conversationSubset,
                                  conversationState: ctx.conversationState,
                                  assignedUserId: ctx.selectedAssignee,
                                  selectedChannels: ctx.selectedChannels,
                                  selectedTags: ctx.selectedTags,
                                  selectedAttribute: ctx.selectedAttribute,
                                  defaultPageId: ctx.activeBotDefaultPageId,
                                  remotePageId: ctx.remoteFbPageId,
                                  page: fetchStatus.nextPageToFetch,
                                  perPage: 20,
                                };

                                return send({
                                  type: 'LC_CONVERSATIONS_GET_MORE_REDIRECT',
                                  payload: fetchParams,
                                });
                              }),
                            },
                          },
                        },
                      },
                      on: {
                        LC_VIEW_UPDATE: {
                          // Note: Transition to itself cancels invoked promises, sets
                          // children states to thier init states
                          // and re-runs entry/exit events
                          target: 'active',
                          internal: false,
                          // @ts-ignore Note: xState typing for pure effects seems to be wrong with latest TS versions
                          actions: pure((ctx, evt) => {
                            const updatedContext = {
                              ...ctx,
                              ...evt.payload.viewUpdate,
                            };

                            (() => {
                              if (
                                updatedContext.conversationState !== 'main' &&
                                updatedContext.conversationState !== 'follow_up'
                              ) {
                                updatedContext.conversationSubset = 'filtered';
                                return;
                              }
                              if (
                                updatedContext.selectedAssignee !== 'all' ||
                                updatedContext.selectedChannels.length ||
                                updatedContext.selectedTags.length ||
                                !!updatedContext.selectedAttribute
                              ) {
                                updatedContext.conversationSubset = 'filtered';
                                return;
                              }
                              updatedContext.conversationSubset =
                                updatedContext.conversationState;
                            })();

                            if (
                              updatedContext.conversationSubset === 'filtered'
                            ) {
                              updatedContext.filteredConversationRemoteUserIds =
                                new Set();
                              updatedContext.fetchStatus = {
                                ...updatedContext.fetchStatus,
                                filtered: fetchStatus,
                              };
                            }

                            const actionsToReturn: ActionObject<
                              Partial<LcContext>,
                              LcEventSchema
                            >[] = [
                              assign({
                                ...updatedContext,
                              }),
                            ];

                            if (!evt.payload.skipStoringAttributes) {
                              actionsToReturn.push(
                                send({
                                  type: 'LC_STORE_VIEW',
                                  payload: evt.payload.viewUpdate,
                                }),
                              );
                            }

                            actionsToReturn.push(
                              send(
                                { type: 'LC_CONVERSATIONS_GET_MORE' },
                                { delay: 10 },
                              ),
                            );

                            return actionsToReturn;
                          }),
                        },
                      },
                    },
                  },
                },
              },
              on: {
                LC_CONVERSATIONS: {
                  actions: 'lcConversations',
                },

                LC_LOCATION_SEARCH_CHANGE: {
                  actions: 'locationSearchChagne',
                },
                LC_CONVERSATION_AND_CONTACT_CREATE: {
                  actions: 'conversationAndContactCreate',
                },

                LC_CONVERSATION_UPDATE: {
                  actions: pure((_ctx, evt) => {
                    if (evt.payload.remoteUserId) {
                      return send(
                        {
                          type: 'LC_CONVERSATION_UPDATE',
                          payload: evt.payload,
                        },
                        { to: `conversation-${evt.payload.remoteUserId}` },
                      );
                    }
                  }),
                },

                LC_CONVERSATIONS_REFRESH: {
                  // @ts-ignore Note: xState typing for pure effects seems to be wrong with latest TS versions
                  actions: pure((ctx, evt) => {
                    if (evt.payload === 'all') {
                      return [
                        send({
                          type: 'LC_REMOTE_USER_ID_CHANGE',
                          payload: undefined,
                        }),
                        send({ type: 'LC_VIEW_BULK_RESET' }, { delay: 50 }),
                      ];
                    }

                    const linkedRemoteUserIds = Object.entries(
                      ctx.conversationsRefs,
                    )
                      .filter(([_k, v]) => {
                        return v?.state?.context.model.linkedContacts?.some(
                          lc => evt.payload.indexOf(lc.remoteUserId) > 0,
                        );
                      })
                      .map(([k, _v]) => k);

                    const remoteUserIdsToRefresh = [
                      ...evt.payload,
                      ...linkedRemoteUserIds,
                    ];

                    const actionsToReturn: ActionObject<
                      Partial<LcContext>,
                      LcEventSchema
                    >[] = [];
                    for (const remoteUserId of remoteUserIdsToRefresh) {
                      actionsToReturn.push(
                        send(
                          { type: 'LC_CONVERSATION_REDOWNLOAD' },
                          { to: `conversation-${remoteUserId}` },
                        ),
                      );
                    }

                    return actionsToReturn;
                  }),
                },

                LC_CONTACT_LAST_ACTIVE_UPDATE: {
                  actions: (ctx, evt) => {
                    const contactRemoteId = evt.payload.remoteUserId;
                    const contactActor =
                      contactRemoteId && ctx.contactsRefs[contactRemoteId];

                    if (!contactActor) {
                      return;
                    }

                    const lastActiveVariable =
                      contactActor.state.context.model.variables.find(
                        v => v.variable.name === 'last active',
                      );
                    if (!lastActiveVariable) {
                      return;
                    }

                    const parsedLastActiveTime = moment.utc(
                      lastActiveVariable?.value,
                      'YYYY-MM-DD HH:mm:ss UTC',
                      true,
                    );
                    if (!parsedLastActiveTime.isValid()) {
                      return;
                    }

                    const latestIncomingMessageTime = moment.utc(
                      Number(evt.payload.time),
                    );
                    if (
                      latestIncomingMessageTime.isSameOrBefore(
                        parsedLastActiveTime,
                      )
                    ) {
                      return;
                    }

                    const variables =
                      contactActor.state.context.model.variables.map(v =>
                        v.variable.name === 'last active'
                          ? {
                              ...lastActiveVariable,
                              value: `${latestIncomingMessageTime.format(
                                'YYYY-MM-DD HH:mm:ss',
                              )} UTC`,
                            }
                          : v,
                      );
                    contactActor.send({
                      type: 'LC_CONTACT_UPDATE',
                      payload: {
                        model: {
                          ...contactActor.state.context.model,
                          variables,
                        },
                      },
                    });
                  },
                },
                LC_SEARCH: {
                  target: [
                    '.contactSearch.searching',
                    '.conversationSearch.searching',
                  ],
                  internal: false,
                  cond: ctx => !!ctx.searchString,
                  actions: assign(ctx => {
                    return {
                      ...ctx,
                      foundContacts: [],
                      foundConversations: undefined,
                    };
                  }),
                },
                LC_CONVERSATIONS_DELETE: {
                  actions: pure((ctx, evt) => {
                    const remoteUserIds =
                      evt.payload === 'all'
                        ? Object.keys(ctx.contactsRefs)
                        : evt.payload;

                    const fetchStatus =
                      evt.payload === 'all'
                        ? initialFetchStatus
                        : ctx.fetchStatus;

                    const conversationsRefs = { ...ctx.conversationsRefs };
                    const contactsRefs = { ...ctx.contactsRefs };

                    for (const remoteUserId of remoteUserIds) {
                      if (!conversationsRefs[remoteUserId]) {
                        conversationsRefs[remoteUserId]?.stop?.();
                        delete conversationsRefs[remoteUserId];
                      }
                      if (!contactsRefs[remoteUserId]) {
                        contactsRefs[remoteUserId]?.stop?.();
                        delete contactsRefs[remoteUserId];
                      }
                    }

                    const mostRecentConversationsRemoteUserIds =
                      remoteUserIds.some(remoteUserId =>
                        ctx.mostRecentConversationsRemoteUserIds.has(
                          remoteUserId,
                        ),
                      )
                        ? new Set(
                            [
                              ...ctx.mostRecentConversationsRemoteUserIds,
                            ].filter(
                              remoteUserId =>
                                remoteUserIds.indexOf(remoteUserId) === -1,
                            ),
                          )
                        : ctx.mostRecentConversationsRemoteUserIds;

                    const filteredConversationRemoteUserIds =
                      remoteUserIds.some(remoteUserId =>
                        ctx.filteredConversationRemoteUserIds.has(remoteUserId),
                      )
                        ? new Set(
                            [...ctx.filteredConversationRemoteUserIds].filter(
                              remoteUserId =>
                                remoteUserIds.indexOf(remoteUserId) === -1,
                            ),
                          )
                        : ctx.filteredConversationRemoteUserIds;

                    return assign({
                      conversationsRefs,
                      contactsRefs,
                      mostRecentConversationsRemoteUserIds,
                      filteredConversationRemoteUserIds,
                      fetchStatus,
                    });
                  }),
                },
              },
            },
          },
          on: {
            LC_CONTEXT_UPDATE: {
              // Note: updating context of spawned actors
              actions: assign((ctx, evt) => {
                if (
                  evt.payload.activeChatbotId &&
                  evt.payload.activeChatbotId !== ctx.activeChatbotId
                ) {
                  Object.values(ctx.contactsRefs).forEach(a => {
                    a.send({
                      type: 'LC_CONTACT_UPDATE',
                      payload: { activeChatbotId: evt.payload.activeChatbotId },
                    });

                    // Reloading active selected contact if it is empty
                    if (
                      a.state.context.model.remoteId === ctx.remoteUserId &&
                      !a.state.context.model.createdAt
                    ) {
                      setTimeout(() => {
                        a.send({
                          type: 'LC_CONTACT_REFRESH',
                          payload: a.state.context.model.remoteId,
                        });
                      }, 10);
                    }
                  });
                }
                if (
                  evt.payload.connectedPage &&
                  !isEqual(evt.payload.connectedPage, ctx.connectedPage)
                ) {
                  Object.values(ctx.contactsRefs).forEach(a => {
                    a.send({
                      type: 'LC_CONTACT_UPDATE',
                      payload: { connectedPage: evt.payload.connectedPage },
                    });
                    // Reloading active selected contact if it is empty
                    if (
                      a.state.context.model.remoteId === ctx.remoteUserId &&
                      !a.state.context.model.createdAt
                    ) {
                      setTimeout(() => {
                        a.send({
                          type: 'LC_CONTACT_REFRESH',
                          payload: a.state.context.model.remoteId,
                        });
                      }, 10);
                    }
                  });
                }
                return { ...ctx, ...evt.payload };
              }),
            },
            LC_REMOTE_FB_PAGE_ID_CHANGE: {
              target: '#mainLogic.init',
              internal: false,
              actions: assign((ctx, evt) => ({
                remoteFbPageId: evt.payload,
              })),
            },
            LC_BOT_ID_CHANGE: {
              target: '#mainLogic.init',
              internal: false,
              cond: ctx => ctx.activeChatbotId === -1,
              actions: assign((ctx, evt) => {
                return { activeChatbotId: evt.payload };
              }),
            },
            LC_BOT_DEFAULT_PAGE_ID_CHANGE: {
              target: '#mainLogic.init',
              internal: false,
              actions: assign((ctx, evt) => ({
                activeBotDefaultPageId: evt.payload,
              })),
            },
            LC_VIEW_BULK_RESET: {
              target: '#mainLogic.init',
              internal: false,
            },
          },
        },
      },
      on: {
        LC_LAST_CONVERSATION_UPDATE: {
          actions: assign(() => {
            return {
              lastSSEUpdate: Date.now(),
            };
          }),
        },
      },
    },
    {
      services: {
        storedViewFetching: async () => {
          const viewUpdate = await liveChatCRUDActions.getStoredView();
          return viewUpdate;
        },
        storedViewSaving: async (ctx, evt: LcStoreView) => {
          return liveChatCRUDActions.storeViewAttributes(evt.payload);
        },
        contactSearch: async ctx => {
          return liveChatCRUDActions.searchContacts(
            ctx.botLanguage,
            ctx.searchString,
          );
        },
        conversationSearch: async ctx => {
          return liveChatCRUDActions.searchConversations(
            ctx.botLanguage,
            ctx.searchString,
          );
        },
        remoteUserUpdate: async (ctx, evt: LcRemoteUserIdChange) => {
          const remoteUserId = evt.payload;
          if (ctx.remoteFbPageId && ctx.sessionSuffix && remoteUserId) {
            const response =
              await liveChatCRUDActions.updateSelectedRemoteUserId({
                remoteUserId,
                sessionSuffix: ctx.sessionSuffix,
              });
            const parsedData = await response?.json();
            return remapMessages(parsedData);
          }
        },
        searchResultsMessagesFetching: async (
          ctx,
          evt: LcGetSearchResultSession,
        ) => {
          if (!evt.payload.remUserId) return;

          const res = await liveChatCRUDActions.getConversationSession({
            remoteUserId: evt.payload.remUserId,
            selectedSessionId: evt.payload.sessionId,
          });

          if (!res) return;
          const responseJson = await res.json();
          return {
            reset: evt.payload.remUserId,
            addingOlder: evt.payload.addingOlder,
            remappedMessages: remapMessages(responseJson),
          };
        },
        loadBatchConversationMessages: async ctx => {
          const res = await liveChatCRUDActions.batchGetPaginatedMessages(
            ctx.currentConversationsBatchToLoad,
          );

          if (!res) return;
          const responseJson = await res.json();
          return responseJson;
        },
        conversationsFiltering: async (ctx, evt: LcRefilterRedirect) => {
          const res = await liveChatCRUDActions.getFilteredConversations(
            evt.payload.filterParameters,
          );

          if (res) {
            return {
              conversations: res,
              remoteUserIds: evt.payload.filterParameters.remoteUserIds,
            };
          }
        },
        loadFollowUps: async ctx => {
          if (!ctx.remoteFbPageId) return;
          const result = await liveChatCRUDActions.getFollowUpConversations({
            conversationState: 'follow_up',
            remotePageId: ctx.remoteFbPageId,
            page: 0,
            perPage: 20,
          });

          if (result?.conversations) {
            return {
              conversations: result.conversations,
            };
          }
        },
        conversationsLoading: async (
          ctx,
          evt: LcConversationsGetMoreRedirect,
        ) => {
          const conversations = await liveChatCRUDActions.getMoreConversations(
            evt.payload,
          );

          return {
            conversations,
            fetchParameters: evt.payload,
          };
        },
      },
      actions: {
        clearSession: (ctx, evt: LcEventSourceError) =>
          sessionCRUDActions.clear(evt.payload.error.status?.toString()),
        sseSuccess: assign((ctx, evt) => {
          const parsedData: OnSuccessData = JSONUtils.safeParse(
            (evt as LcSseSuccess).payload.data,
          );
          if (parsedData) {
            const { sessionSuffix } = parsedData;
            const initialConnection = !ctx.sessionSuffix;

            if (initialConnection && ctx.remoteUserId) {
              liveChatCRUDActions.updateSelectedRemoteUserId({
                remoteUserId: ctx.remoteUserId,
                sessionSuffix,
              });
            }

            return {
              ...ctx,
              sessionSuffix,
            };
          }

          return {};
        }),
        lcConversations: pure((ctx, evt: LcConversations) => {
          const actionsToReturn: ActionObject<
            Partial<LcContext>,
            LcEventSchema
          >[] = [];
          const conversations = evt.payload;

          if (conversations) {
            const newConversationRefs = {};
            const newContactsRefs = {};
            let notLinkedContactIdsToAdd: string[] = [];

            conversations.forEach(conv => {
              const conversationContactContext = ctx.contactsRefs[
                conv.remoteUserId
              ]?.state?.context as ContactContext;

              // Updating contact conversation muting state
              if (conv.mutedTill && conversationContactContext) {
                const { muted, mutedTill } = conv;
                const mutedTillFormatted = moment(Number(mutedTill))
                  .utc()
                  .toISOString();
                if (
                  conversationContactContext &&
                  mutedTillFormatted !==
                    conversationContactContext.model.conversation?.mutedTill
                ) {
                  const conversation = {
                    ...conversationContactContext.model.conversation,
                    mutedTill: mutedTillFormatted,
                    muted,
                  } as Conversation;

                  actionsToReturn.push(
                    send(
                      {
                        type: 'LC_CONTACT_UPDATE',
                        payload: {
                          model: {
                            ...conversationContactContext.model,
                            conversation,
                          },
                        },
                      },
                      { to: `contact-${conv.remoteUserId}` },
                    ),
                  );
                }
              }

              // Creating new contact machines for newly loaded conversations
              if (!conversationContactContext) {
                const notYetAddedLinkedContactIds =
                  conv.linkedContacts
                    ?.filter(c => {
                      return (
                        !ctx.contactsRefs[c.remoteUserId] &&
                        !newContactsRefs[c.remoteUserId]
                      );
                    })
                    .map(c => c.remoteUserId) || [];

                notLinkedContactIdsToAdd = [
                  ...new Set([
                    ...notLinkedContactIdsToAdd,
                    ...notYetAddedLinkedContactIds,
                  ]),
                ];

                for (const remoteUserId of [
                  conv.remoteUserId,
                  ...notYetAddedLinkedContactIds,
                ]) {
                  const contactMachineContext = {
                    model: {
                      ...EmptySelectedContact,
                      remoteId: remoteUserId,
                    },
                    archivedMessages: [],
                    needsToScrollRef: false,
                    selectedSessionId: undefined,
                    nextSessionId: undefined,
                    previousSessionId: undefined,
                    connectedPage: ctx.connectedPage,
                    activeChatbotId: ctx.activeChatbotId,
                  };

                  newContactsRefs[remoteUserId] = spawn(
                    createContactMachine(
                      contactMachineContext,
                      contactsCRUDActions,
                      liveChatCRUDActions,
                    ),

                    {
                      name: `contact-${remoteUserId}`,
                      sync: true,
                    },
                  );

                  // Creating new conversation machines for newly loaded conversations
                  if (!ctx.conversationsRefs[remoteUserId]) {
                    const conversationMachineContext = {
                      ...initialConversationContext,
                      model:
                        conv.remoteUserId === remoteUserId
                          ? { ...conv }
                          : { remoteUserId },
                    };

                    newConversationRefs[remoteUserId] = spawn(
                      createConversationMachine(
                        // @ts-ignore Note: hack to quickly create machine without full data
                        conversationMachineContext,
                        liveChatCRUDActions,
                      ),
                      {
                        name: `conversation-${remoteUserId}`,
                        sync: true,
                      },
                    );

                    if (conv.remoteUserId !== remoteUserId) {
                      actionsToReturn.push(
                        send(
                          { type: 'LC_CONVERSATION_REDOWNLOAD' },
                          {
                            to: `conversation-${remoteUserId}`,
                            delay: 10,
                          },
                        ),
                      );
                    }
                  } else {
                    const storedConversation = ctx.conversationsRefs[
                      conv.remoteUserId
                    ]?.state?.context?.model as
                      | ConversationContext['model']
                      | undefined;

                    if (!isEqual(storedConversation, conv)) {
                      actionsToReturn.push(
                        send(
                          {
                            type: 'LC_CONVERSATION_REPLACE',
                            payload: conv,
                          },
                          { to: `conversation-${conv.remoteUserId}` },
                        ),
                      );
                    }
                  }
                }
              }
              if (ctx.conversationsRefs[conv.remoteUserId]) {
                const storedConversation = ctx.conversationsRefs[
                  conv.remoteUserId
                ]?.state?.context?.model as
                  | ConversationContext['model']
                  | undefined;

                if (!isEqual(storedConversation, conv)) {
                  actionsToReturn.push(
                    send(
                      {
                        type: 'LC_CONVERSATION_REPLACE',
                        payload: conv,
                      },
                      { to: `conversation-${conv.remoteUserId}` },
                    ),
                  );
                }
              }
            });

            if (Object.keys(newContactsRefs).length) {
              actionsToReturn.push(
                assign({
                  contactsRefs: {
                    ...ctx.contactsRefs,
                    ...newContactsRefs,
                  },
                }),
              );
            }

            if (Object.keys(newConversationRefs).length) {
              actionsToReturn.push(
                assign({
                  conversationsRefs: {
                    ...ctx.conversationsRefs,
                    ...newConversationRefs,
                  },
                }),
              );
            }

            const currentRemoteUserIds = [
              ...conversations.map(conversation => conversation.remoteUserId),
              ...notLinkedContactIdsToAdd,
            ];

            const { mostRecentConversationsRemoteUserIds } = ctx;
            if (
              currentRemoteUserIds.some(
                remoteUserId =>
                  !mostRecentConversationsRemoteUserIds.has(remoteUserId),
              )
            ) {
              const newMostRecentConversationsRemoteUserIds = new Set([
                ...currentRemoteUserIds,
                ...mostRecentConversationsRemoteUserIds,
              ]);

              actionsToReturn.push(
                assign({
                  mostRecentConversationsRemoteUserIds:
                    newMostRecentConversationsRemoteUserIds,
                }),
              );
            }
          }

          return actionsToReturn;
        }),
        conversationAndContactCreate: pure(
          (ctx, evt: LcConversationsAndContactCreation) => {
            const actionsToReturn: ActionObject<
              Partial<LcContext>,
              LcEventSchema
            >[] = [];

            const remoteUserId = evt.payload;

            const newConversationRefs = {};
            const newContactsRefs = {};

            const contactMachineContext = {
              model: {
                ...EmptySelectedContact,
                remoteId: remoteUserId,
              },
              archivedMessages: [],
              needsToScrollRef: false,
              selectedSessionId: undefined,
              nextSessionId: undefined,
              previousSessionId: undefined,
              connectedPage: ctx.connectedPage,
              activeChatbotId: ctx.activeChatbotId,
            };

            newContactsRefs[remoteUserId] = spawn(
              createContactMachine(
                contactMachineContext,
                contactsCRUDActions,
                liveChatCRUDActions,
              ),
              {
                name: `contact-${remoteUserId}`,
                sync: true,
              },
            );

            const conversationMachineContext = {
              ...initialConversationContext,
              model: { remoteUserId },
            };

            newConversationRefs[remoteUserId] = spawn(
              createConversationMachine(
                // @ts-ignore Note: hack to quickly create machine without full data
                conversationMachineContext,
                liveChatCRUDActions,
              ),
              {
                name: `conversation-${remoteUserId}`,
                sync: true,
              },
            );
            actionsToReturn.push(
              assign({
                contactsRefs: { ...ctx.contactsRefs, ...newContactsRefs },
                conversationsRefs: {
                  ...ctx.conversationsRefs,
                  ...newConversationRefs,
                },
              }),
            );

            actionsToReturn.push(
              send(
                { type: 'LC_CONTACT_REFRESH', payload: remoteUserId },
                {
                  to: `contact-${remoteUserId}`,
                  delay: 10,
                },
              ),
            );

            actionsToReturn.push(
              send(
                { type: 'LC_CONVERSATION_REDOWNLOAD' },
                {
                  to: `conversation-${remoteUserId}`,
                  delay: 10,
                },
              ),
            );
            return actionsToReturn;
          },
        ),
        locationSearchChagne: pure(ctx => {
          const actionsToReturn: ActionObject<
            Partial<LcContext>,
            LcEventSchema
          >[] = [];

          const { location } = history;

          const parsed = queryString.parse(location.search.toLowerCase());
          const [, , , path, remoteUserId] = location.pathname.split('/');

          let newRemoteUserId: string | undefined;

          if (!parsed.remoteuserid && path === 'live-chat' && remoteUserId) {
            newRemoteUserId = remoteUserId;
          }
          newRemoteUserId =
            typeof parsed.remoteuserid === 'string'
              ? parsed.remoteuserid.replace(/ /g, '+')
              : undefined;

          if (newRemoteUserId !== ctx.remoteUserId) {
            actionsToReturn.push(
              assign({ ...ctx, remoteUserId: newRemoteUserId }),
              send({
                type: 'LC_REMOTE_USER_ID_CHANGE',
                payload: newRemoteUserId,
              }),
            );
          }

          if (newRemoteUserId && !ctx.conversationsRefs[newRemoteUserId]) {
            actionsToReturn.push(
              send({
                type: 'LC_CONVERSATION_AND_CONTACT_CREATE',
                payload: newRemoteUserId,
              }),
            );
          }
          return actionsToReturn;
        }),
        remoteUserIdChange: (ctx, evt: LcRemoteUserIdChange) => {
          const queryString = evt.payload ? `?remoteUserId=${evt.payload}` : '';
          history.push(`${history.location.pathname}${queryString}`);
        },
        stopActors: assign(ctx => {
          Object.values(ctx.conversationsRefs).forEach(actor => {
            actor.stop?.();
          });
          Object.values(ctx.contactsRefs).forEach(actor => actor?.stop);
          return {
            conversationsRefs: {},
            contactsRefs: {},
          };
        }),
        batchedConversationMessagesUpdate: pure((ctx, evt) => {
          // Note: need to cast using as since otherwise types fail
          const conversationsMessages =
            (evt as DoneInvokeEvent<undefined | Record<string, any>[]>).data ||
            [];

          const actionsToReturn: ActionObject<
            Partial<LcContext>,
            LcEventSchema
          >[] = [];
          for (const conversationMessages of conversationsMessages) {
            const remappedData = remapMessages(conversationMessages);

            if (
              remappedData?.data?.length &&
              !!ctx.conversationsRefs[remappedData.data[0].remoteUserId]
            ) {
              actionsToReturn.push(
                send(
                  {
                    type: 'LC_CONVERSATIONS_MESSAGES_UPDATE',
                    payload: {
                      data: remappedData,
                      isRestCall: true,
                    },
                  },
                  {
                    to: `conversation-${remappedData.data[0].remoteUserId}`,
                  },
                ),
              );
            }
          }

          return actionsToReturn;
        }),
        contextReset: assign(ctx => {
          return {
            ...ctx,
            lastSSEUpdate: Date.now(),
            currentConversationsBatchToLoad: [],
            fetchStatus: initialFetchStatus,
            filteredConversationRemoteUserIds: new Set(),
            mostRecentConversationsRemoteUserIds: new Set(),
            conversationSubset: 'main',
            conversationState: 'main',
            selectedChannels: [],
            selectedTags: [],
            selectedAttribute: null,
            selectedAssignee: 'all',
            onlineRemoteUserIds: new Set(),

            sessionSuffix: undefined,
            activeContact: undefined,
          };
        }),
      },
      guards: {
        hasBatchedConversationMessagesToLoad: ctx =>
          ctx.batchConversationMessagesToLoad.length > 0,
        hasNoBatchedConversationMessagesToLoad: ctx =>
          ctx.batchConversationMessagesToLoad.length === 0,
      },
    },
  );
